/**
 * kit-v3 — Kit V3 product page: formula picker + landed-cost estimator.
 *
 * Two independent axes drive the page:
 *   FORMULE  — maps 1:1 to a Shopify variant. This is what goes in the cart.
 *   LIVRAISON — France, Europe, UK, Canada, US, Australia.
 *               Informational: it breaks down shipping and customs so the
 *               buyer knows the landed cost up front. Shopify charges the real
 *               shipping and duties at checkout, so nothing here is submitted.
 *
 * Currency: every amount is in cents of the *presentment* currency, the one
 * Shopify is already serving this visitor. The variant price comes converted
 * from Liquid; the shipping and customs amounts come from the section settings,
 * which carry one set of values per alternate currency (USD, CAD, AUD). So a
 * visitor shopping in any of them sees that currency everywhere, with no FX
 * maths here.
 *
 * Border charges are the exception: Canadian GST, Australian GST and the
 * Australian Border Force declaration fees are levied by the destination in its
 * own money, whoever paid what. They come through the locales as plain strings
 * and are never added to a total.
 */

const REGIONS = ['fr', 'eu', 'uk', 'ca', 'us', 'au'];

const DEFAULT_CURRENCY_DECIMALS = 2;

/** Currencies whose minor unit is not 1/100. Mirrors the table in facets.js. */
const CURRENCY_DECIMALS = {
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  UYI: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
};

function formatCents(cents, thousands, decimal, precision) {
  const rounded = (cents / 100).toFixed(precision);
  let [whole, frac] = rounded.split('.');
  if (!whole) whole = '0';
  if (!frac) frac = '';
  whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousands);
  return precision <= 0 ? whole : whole + decimal + frac.padEnd(precision, '0');
}

/**
 * Renders an amount the way the `money` Liquid filters do, from the store's
 * own money_format. Same implementation as FacetStatusComponent#formatMoney in
 * facets.js, which is private to that component.
 *
 * @param {number} cents amount in hundredths of the presentment currency
 * @param {string} template the store's money_format
 * @param {string} currency presentment currency ISO code
 * @param {boolean} dropZeroCents behave like `money_without_trailing_zeros`
 */
function formatMoney(cents, template, currency, dropZeroCents) {
  const base = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;
  const whole = dropZeroCents && cents % 100 === 0;

  return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (placeholder === 'currency') return currency;

    let thousands = ',';
    let decimal = '.';
    let precision = base;

    if (placeholder === 'amount') {
      // Most common case — defaults apply.
    } else if (placeholder === 'amount_no_decimals') {
      precision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousands = '.';
      decimal = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousands = '.';
      precision = 0;
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousands = ' ';
      precision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousands = ' ';
      decimal = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousands = ' ';
      decimal = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousands = "'";
      decimal = '.';
    }

    return formatCents(cents, thousands, decimal, whole ? 0 : precision);
  });
}

function esc(s) {
  return String(s == null ? '' : s).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );
}

class KitV3 extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-kit-v3-data]');
    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }

    this.formulas = this.data.formulas || [];
    if (this.formulas.length === 0) return;

    this.formula = Math.max(
      0,
      this.formulas.findIndex((f) => f.available)
    );
    this.region = REGIONS.includes(this.data.region) ? this.data.region : 'fr';
    // Where Shopify says the visitor is, to flag a mismatch later.
    this.initRegion = this.region;
    this.ddpOpen = false;

    this.geoNotice = this.querySelector('[data-k3-geo]');
    this.querySelector('[data-k3-open-country]')?.addEventListener('click', () =>
      this.openCountrySelector()
    );

    this.priceCard = this.querySelector('[data-k3-pricecard]');
    this.variantInput = this.querySelector('[data-k3-variant-id]');
    this.ctaPrice = this.querySelector('[data-k3-cta-price]');
    this.cta = this.querySelector('[data-k3-cta]');

    this.bindGallery();
    this.bindFormulas();
    this.bindRegions();
    this.bindAnchors();
    this.render();
  }

  /** Formats like `money_without_trailing_zeros`, as the Liquid side does. */
  money(cents) {
    return formatMoney(cents, this.data.moneyFormat, this.data.currency, true);
  }

  /* ── derived amounts, all in cents ───────────────────────────────── */

  /*
   * VAT follows the destination, not the visitor. Shipping to France or the EU
   * always carries it; exporting to the UK, Canada, the US or Australia never
   * does. But the price Shopify serves is gross or net depending on the
   * visitor's own market, so both conversions have to work in both directions.
   */

  /** Price including VAT — what a French or EU delivery is charged. */
  gross(f) {
    return this.data.taxesIncluded ? f.priceCents : Math.round(f.priceCents * this.data.vat);
  }

  /** Price excluding VAT — what any export outside the EU is charged. */
  net(f) {
    return this.data.taxesIncluded ? Math.round(f.priceCents / this.data.vat) : f.priceCents;
  }

  totals(f) {
    const gross = this.gross(f);
    const net = this.net(f);
    return {
      fr: gross,
      eu: gross + f.euShip,
      uk: net + f.euShip,
      ca: net + f.caShip,
      us: net + f.usShip + f.usDdp,
      au: net + f.auShip,
    };
  }

  /** Landed cost for the active destination — informational, never submitted. */
  total(f) {
    return this.totals(f)[this.region];
  }

  get active() {
    return this.formulas[this.formula];
  }

  /* ── wiring ──────────────────────────────────────────────────────── */

  bindGallery() {
    const main = this.querySelector('[data-k3-gallery-img]');
    const thumbs = Array.from(this.querySelectorAll('[data-k3-thumb]'));
    if (!main || thumbs.length === 0) return;

    thumbs.forEach((btn, i) => {
      btn.addEventListener('click', () => {
        main.src = btn.dataset.src;
        main.alt = btn.dataset.alt || '';
        main.style.objectPosition = btn.dataset.pos || 'center';
        thumbs.forEach((b, j) => {
          b.classList.toggle('is-active', i === j);
          b.setAttribute('aria-current', i === j ? 'true' : 'false');
        });
      });
    });
  }

  bindFormulas() {
    this.formulaBtns = Array.from(this.querySelectorAll('[data-k3-formula]'));
    this.formulaBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const i = Number(btn.dataset.k3Formula);
        if (Number.isNaN(i) || i === this.formula) return;
        this.formula = i;
        this.render();
      });
    });
  }

  bindRegions() {
    this.regionBtns = Array.from(this.querySelectorAll('[data-k3-region]'));
    this.regionBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const r = btn.dataset.k3Region;
        if (!REGIONS.includes(r) || r === this.region) return;
        this.region = r;
        this.ddpOpen = false;
        this.render();
      });
    });
  }

  /**
   * Opens the header's own country selector — the only control that actually
   * changes the Shopify market, and therefore the currency and the prices.
   * Desktop shows a dropdown; on mobile it lives inside the menu drawer, which
   * has to be opened first.
   *
   * @returns {boolean} whether a selector was found and opened
   */
  openCountrySelector() {
    const visible = (el) => el instanceof HTMLElement && el.offsetParent !== null;
    const header = document.getElementById('header-group') || document;

    const dropdown = header.querySelector('.dropdown-localization__button');
    if (visible(dropdown)) {
      dropdown.scrollIntoView({ block: 'center', behavior: 'smooth' });
      dropdown.click();
      return true;
    }

    const burger = header.querySelector('header-drawer summary.header__icon--menu');
    if (visible(burger)) {
      burger.click();
      // The drawer has to finish opening before its country panel can be hit.
      window.setTimeout(() => {
        const inDrawer = header.querySelector('.drawer-localization__button');
        if (visible(inDrawer)) inDrawer.click();
      }, 300);
      return true;
    }

    return false;
  }

  /** "Pourquoi ce délai ?", "Détails", "En savoir plus" — smooth scroll. */
  bindAnchors() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('[data-k3-goto]');
      if (!link) return;
      e.preventDefault();
      // getElementById rather than a '#id' selector: section ids carry '--'
      // and '__', which would need escaping in querySelector.
      const el = document.getElementById(link.dataset.k3Goto);
      if (el) {
        window.scrollTo({
          top: el.getBoundingClientRect().top + window.scrollY - 16,
          behavior: 'smooth',
        });
      }
    });
  }

  /* ── render ──────────────────────────────────────────────────────── */

  render() {
    const f = this.active;
    const nohl = !!f.noHeadlight;

    // Formula buttons: active state + region-dependent price line.
    this.formulaBtns.forEach((btn, i) => {
      const on = i === this.formula;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const slot = btn.querySelector('[data-k3-formula-price]');
      if (slot) slot.textContent = this.formulaHint(this.formulas[i]);
    });

    this.regionBtns.forEach((btn) => {
      const on = btn.dataset.k3Region === this.region;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    // Warn as soon as the chosen destination stops matching where the visitor
    // actually is — or straight away if their country is none of the four.
    if (this.geoNotice) {
      this.geoNotice.hidden = this.data.regionKnown && this.region === this.initRegion;
    }

    // Cart: only the formula matters.
    if (this.variantInput) this.variantInput.value = f.variantId;
    /*
     * The button states what the click does: the variant price, which is what
     * actually lands in the cart. Shipping is picked at checkout, so promising
     * a landed total here would announce an amount the next step won't charge.
     * The estimator above goes on showing that total.
     */
    if (this.ctaPrice) this.ctaPrice.textContent = this.money(f.priceCents);
    if (this.cta) this.cta.disabled = !f.available;

    if (this.priceCard) this.priceCard.innerHTML = this.priceCardHtml(f);

    // Everything downstream that reads "with / without headlight".
    this.querySelectorAll('[data-k3-nohl-only]').forEach((el) => {
      el.hidden = !nohl;
    });
    this.querySelectorAll('[data-k3-hide-nohl]').forEach((el) => {
      el.hidden = nohl;
    });
    // Le DAP n'a jamais concerné que les États-Unis.
    this.querySelectorAll('[data-k3-us-only]').forEach((el) => {
      el.hidden = this.region !== 'us';
    });
    this.querySelectorAll('[data-k3-lead-weeks]').forEach((el) => {
      el.textContent = f.leadWeeks;
    });
    this.querySelectorAll('[data-k3-lead-weeks-text]').forEach((el) => {
      el.textContent = f.leadWeeksText;
    });
    this.querySelectorAll('[data-k3-lead-title]').forEach((el) => {
      el.textContent = f.leadTitle;
    });
    this.querySelectorAll('[data-k3-ship-step]').forEach((el) => {
      el.textContent = f.shipStep;
    });

    const ddpToggle = this.priceCard?.querySelector('[data-k3-ddp-toggle]');
    if (ddpToggle) {
      ddpToggle.addEventListener('click', () => {
        this.ddpOpen = !this.ddpOpen;
        this.render();
      });
    }
  }

  /** The small line under each formula name, in the active region's terms. */
  formulaHint(f) {
    const C = this.data.copy;
    const t = this.totals(f);
    if (this.region === 'us') return this.money(t.us) + ' ' + C.hintDdp;
    if (this.region === 'uk') return this.money(t.uk) + ' ' + C.hintExclDuty;
    if (this.region === 'ca') return this.money(t.ca) + ' ' + C.hintExclTax;
    if (this.region === 'au') return this.money(t.au) + ' ' + C.hintExclTax;
    if (this.region === 'eu') return this.money(t.eu) + ' ' + C.hintDelivered;
    return this.money(t.fr) + ' ' + C.hintTax;
  }

  priceCardHtml(f) {
    const C = this.data.copy;
    const t = this.totals(f);

    const line = (label, hint, value) =>
      `<div class="rl-k3__line"><span>${label}` +
      (hint ? `<span class="rl-k3__line-hint">${hint}</span>` : '') +
      `</span><span class="rl-k3__line-v">${value}</span></div>`;

    const totalRow = (key, value) =>
      `<div class="rl-k3__total-row"><span class="rl-k3__total-k">${key}</span>` +
      `<span class="rl-k3__price-big">${value}</span></div>`;

    if (this.region === 'fr') {
      return `
        <div class="rl-k3__price-card">
          <div class="rl-k3__price-main">
            <div>
              <div class="rl-k3__price-name">${esc(f.label)}</div>
              <div class="rl-k3__price-sub">${esc(C.frSub)}</div>
            </div>
            <div class="rl-k3__price-big">${this.money(t.fr)}<span class="rl-k3__ttc">${esc(C.taxTag)}</span></div>
          </div>
        </div>`;
    }

    if (this.region === 'eu') {
      return `
        <div class="rl-k3__price-card">
          ${line(esc(f.label), esc(C.vatIncl), this.money(this.gross(f)))}
          ${line(esc(C.euShipLabel), '', this.money(f.euShip))}
          ${totalRow(esc(C.totalDelivered), this.money(t.eu))}
        </div>`;
    }

    if (this.region === 'uk') {
      return `
        <div class="rl-k3__price-card">
          ${line(esc(f.label), esc(C.vatExclUk), this.money(this.net(f)))}
          ${line(esc(C.ukShipLabel), '', this.money(f.euShip))}
          ${line(esc(C.ukDutyLabel), esc(C.ukDutyHint), esc(C.ukDutyValue))}
          ${totalRow(esc(C.totalExclDuty), this.money(t.uk))}
        </div>`;
    }

    if (this.region === 'ca') {
      // No VAT on export, but Canadian federal GST falls due on import — the
      // carrier collects it on delivery, so it is flagged rather than totalled.
      return `
        <div class="rl-k3__price-card">
          ${line(esc(f.label), esc(C.vatExclCa), this.money(this.net(f)))}
          ${line(esc(C.caShipLabel), '', this.money(f.caShip))}
          ${line(esc(C.caTaxLabel), esc(C.caTaxHint), esc(C.caTaxValue))}
          ${totalRow(esc(C.totalExclTax), this.money(t.ca))}
          <div class="rl-k3__us-note">${esc(C.caTaxNote)}</div>
        </div>`;
    }

    if (this.region === 'au') {
      // Two separate charges fall due at the Australian border: GST and the
      // Border Force declaration fee. Both are quoted in AUD whatever the buyer
      // paid in, and neither is ours to collect — so neither joins the total.
      return `
        <div class="rl-k3__price-card">
          ${line(esc(f.label), esc(C.vatExclAu), this.money(this.net(f)))}
          ${line(esc(C.auShipLabel), '', this.money(f.auShip))}
          ${line(esc(C.auTaxLabel), esc(C.auTaxHint), esc(C.auTaxValue))}
          ${line(esc(C.auDeclLabel), esc(C.auDeclHint), esc(C.auDeclValue))}
          ${totalRow(esc(C.totalExclTax), this.money(t.au))}
          <div class="rl-k3__us-note">${esc(C.auTaxNote)}</div>
        </div>`;
    }

    /*
     * United States — one shipping method only, DDP. The DAP rate was withdrawn
     * because the carrier's charges on arrival proved high and unpredictable,
     * so there is no incoterm left to choose: duties are settled before the
     * parcel leaves. Section 04 says so to the buyer.
     */
    const body = `
        <div class="rl-k3__price-card">
          ${line(esc(f.label), esc(C.vatExclUs), this.money(this.net(f)))}
          ${line(esc(C.usShipLabel), esc(C.usShipHint), this.money(f.usShip))}
          <div class="rl-k3__line${this.ddpOpen ? ' rl-k3__line--open' : ''}">
            <span>${esc(C.ddpFeeLabel)}
              <button type="button" class="rl-k3__inline-link" data-k3-ddp-toggle>
                ${this.ddpOpen ? esc(C.hideDetail) : esc(C.showDetail)}
              </button>
            </span>
            <span class="rl-k3__line-v">${this.money(f.usDdp)}</span>
          </div>
          ${
            this.ddpOpen
              ? `<div class="rl-k3__ddp-box">
                   <div class="rl-k3__ddp-row"><span>${esc(C.usDutyLabel)}</span><span class="rl-k3__line-v">${this.money(f.usDuty)}</span></div>
                   <div class="rl-k3__ddp-row"><span>${esc(C.usHandlingLabel)}</span><span class="rl-k3__line-v">${this.money(f.usDdp - f.usDuty)}</span></div>
                   <p class="rl-k3__ddp-txt">${esc(f.ddpNote)}
                     <button type="button" class="rl-k3__inline-link" data-k3-goto="${this.data.usSectionId}">${esc(C.learnMore)}</button>
                   </p>
                 </div>`
              : ''
          }
          ${totalRow(esc(C.totalDelivered), this.money(t.us))}
          <div class="rl-k3__us-note">${esc(C.ddpReassure)}</div>
        </div>`;

    return `<div class="rl-k3__us-wrap">${body}</div>`;
  }
}

customElements.define('kit-v3', KitV3);
